import React, { useCallback, useEffect, useState } from 'react';
import { FieldInputProps, useField } from 'formik';
import styled from 'styled-components';

import { SearchType } from '../../types';
import { useFetchUnavailableDates } from '../../hooks';
import DatePicker, { DatePickerState } from '../DatePicker';
import { addStep, useCigarette, Values } from '../../context';
import { FIELD_NAMES } from '../../constants';
import { afterDatepickerStep, isDatepickerStep, isTouristsStep } from '../../utils';

type FormDatePickerProps = {
  className?: string;
  searchType: SearchType;
  variant?: 'default' | 'hotel';
} & FieldInputProps<DatePickerState>;

export const FormDatePicker = ({
  name,
  className,
  searchType,
  variant = 'default',
}: FormDatePickerProps) => {
  const [{ value }, { error }, { setValue, setError }] = useField<Values['datepicker']>(name);
  const [{ value: destinationValue }] = useField<Values['destination']>(FIELD_NAMES.destination);
  const [{ value: departureValue }] = useField<Values['departure']>(FIELD_NAMES.departure);
  const {
    state: { isMobile, isWizard, steps },
    dispatch,
  } = useCigarette();
  const [hide, setHide] = useState(!isMobile);
  const [previousSearchType, setPreviousSearchType] = useState<SearchType | undefined>(undefined);

  const { unavailableDates, arrivalDatesDict } = useFetchUnavailableDates({
    toCountry: destinationValue?.iso2,
    fromCity: departureValue?.value,
    searchType,
  });

  const openInWizard = isDatepickerStep(steps) || undefined;
  const openCalendar = !isMobile && !!error;

  const hideDatePicker =
    variant !== 'hotel' &&
    ((!isWizard && hide) || (isWizard && !openInWizard && !afterDatepickerStep(steps)));

  const onClose = useCallback(() => {
    setError(undefined);
  }, [setError]);

  const onChange = (newValue: DatePickerState) => {
    setValue(newValue);
    if (isWizard) {
      dispatch(addStep(FIELD_NAMES.tourists));
    }
  };

  useEffect(() => {
    if (hide && destinationValue) {
      setHide(false);
    }
  }, [destinationValue]);

  useEffect(() => {
    if (value && previousSearchType && previousSearchType !== searchType && variant !== 'hotel') {
      setValue(undefined);
    }
    if (previousSearchType !== searchType) {
      setPreviousSearchType(searchType);
    }
  }, [searchType]);

  if (hideDatePicker) {
    return null;
  }

  return (
    <FormDatePickerContainer
      $isWizard={isWizard}
      $isMobile={isMobile}
      $isTouristsStep={isTouristsStep(steps)}
    >
      <DatePicker
        onChange={onChange}
        isMobile={isMobile}
        isWizard={isWizard}
        initialValue={value}
        className={className}
        open={openInWizard || openCalendar}
        onClose={onClose}
        unavailableDates={unavailableDates}
        arrivalDatesDict={arrivalDatesDict}
        searchType={searchType}
        isFlexDatesOff={variant === 'hotel'}
        variant={variant}
      />
    </FormDatePickerContainer>
  );
};

const FormDatePickerContainer = styled.div<{
  $isWizard?: boolean;
  $isTouristsStep?: boolean;
  $isMobile?: boolean;
}>`
  width: ${({ $isMobile }) => ($isMobile ? '100%' : '320px')};
  ${({ $isTouristsStep, $isMobile }) => ($isTouristsStep || $isMobile ? `order: 1;` : '')};
`;
